/**
 * Settings → Profile Generator: curate the shared asset library (foreground /
 * background images) and review/remove templates. Gated by
 * `manage_profile_generator`. The profile composer itself lives under the
 * Create menu (full-screen editor); this page is only the admin items.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from '../../utils/toast';
import { UploadCloud, Trash2, Image as ImageIcon, LayoutTemplate } from 'lucide-react';
import { T } from '../../components/em/tokens';
import { API_URL } from '../../api/api';
import { SettingsCard } from './settingsUi';
import pgApi from '../ProfileGenerator/pgApi';
import { ASSET_CATEGORIES } from '../ProfileGenerator/pgConstants';

import { confirmDialog } from "../../components/em/confirm";
const resolveUrl = (u) => (!u ? u : u.startsWith('http') || u.startsWith('data:') ? u : `${API_URL}${u}`);

const inputStyle = { height: 36, border: `1px solid ${T.border}`, borderRadius: 8, padding: '0 10px', font: `500 13px ${T.font}`, color: T.fg1, background: T.bgCard, outline: 'none' };
const labelStyle = { font: `600 11px ${T.font}`, color: T.fg6, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5, display: 'block' };

const readImageSize = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: null, height: null });
    img.src = URL.createObjectURL(file);
  });

export default function ProfileGeneratorSettings() {
  const [assets, setAssets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ name: '', type: 'foreground', category: 'Other', tags: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadAssets = () => pgApi.listAssets().then(setAssets).catch(() => {});
  const loadTemplates = () => pgApi.listTemplates().then(setTemplates).catch(() => {});
  useEffect(() => { loadAssets(); loadTemplates(); }, []);

  const upload = async () => {
    if (!file) { toast.error('Choose an image first'); return; }
    setUploading(true);
    try {
      const { width, height } = await readImageSize(file);
      const fd = new FormData();
      fd.append('image', file);
      fd.append('name', form.name || file.name.replace(/\.[^.]+$/, ''));
      fd.append('type', form.type);
      fd.append('category', form.category);
      fd.append('tags', form.tags);
      if (width) fd.append('width', width);
      if (height) fd.append('height', height);
      await pgApi.uploadAsset(fd);
      toast.success('Asset uploaded');
      setForm({ name: '', type: 'foreground', category: 'Other', tags: '' });
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      loadAssets();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeAsset = async (id) => {
    if (!(await confirmDialog('Delete this asset?'))) return;
    try { await pgApi.deleteAsset(id); setAssets((a) => a.filter((x) => x.id !== id)); }
    catch { toast.error('Delete failed'); }
  };

  const removeTemplate = async (id) => {
    if (!(await confirmDialog('Delete this template?'))) return;
    try { await pgApi.deleteTemplate(id); setTemplates((t) => t.filter((x) => x.id !== id)); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <SettingsCard icon={ImageIcon} title="Asset library" subtitle="Shared backgrounds & foreground overlays available in the Profile Generator editor">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Asset name" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={labelStyle}>Type</label>
            <select style={inputStyle} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="foreground">Foreground</option>
              <option value="background">Background</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={labelStyle}>Category</label>
            <select style={inputStyle} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {ASSET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={labelStyle}>Tags (comma separated)</label>
            <input style={inputStyle} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="blue, abstract" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={labelStyle}>Image file</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ font: `500 12px ${T.font}`, color: T.fg3 }} />
          </div>
          <button type="button" onClick={upload} disabled={uploading}
            style={{ height: 38, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, border: 0, borderRadius: 9, cursor: 'pointer', color: '#fff', background: T.gradient, font: `600 13px ${T.font}`, opacity: uploading ? 0.6 : 1 }}>
            <UploadCloud size={16} /> {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, marginTop: 18 }}>
          {assets.length === 0 && <p style={{ font: `400 13px ${T.font}`, color: T.fg5, gridColumn: '1/-1' }}>No assets yet.</p>}
          {assets.map((a) => (
            <div key={a.id} style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', background: T.bgCard }}>
              <div style={{ aspectRatio: '1/1', background: T.bgMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <img src={resolveUrl(a.url)} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <div style={{ padding: '7px 9px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ font: `600 12px ${T.font}`, color: T.fg2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</div>
                  <div style={{ font: `500 10px ${T.font}`, color: T.fg6, textTransform: 'capitalize' }}>{a.type}</div>
                </div>
                <button type="button" onClick={() => removeAsset(a.id)} title="Delete"
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', color: T.danger, flex: '0 0 auto' }}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </SettingsCard>

      <SettingsCard icon={LayoutTemplate} title="Templates" subtitle="Reusable starting points. Save new templates from the editor's “Template” button.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {templates.length === 0 && <p style={{ font: `400 13px ${T.font}`, color: T.fg5, gridColumn: '1/-1' }}>No templates yet.</p>}
          {templates.map((t) => (
            <div key={t.id} style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', background: T.bgCard }}>
              <div style={{ aspectRatio: '4/5', background: T.bgMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {t.thumbnail ? <img src={t.thumbnail} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <LayoutTemplate size={26} color={T.fg6} />}
              </div>
              <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ font: `600 12px ${T.font}`, color: T.fg2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                  {t.category && <div style={{ font: `500 10px ${T.font}`, color: T.fg6 }}>{t.category}</div>}
                </div>
                <button type="button" onClick={() => removeTemplate(t.id)} title="Delete"
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', color: T.danger, flex: '0 0 auto' }}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </SettingsCard>
    </div>
  );
}
